import { useEffect, useState } from "react";

interface UseAccumulatedListOptions<T> {
  /** Current page (whatever zero/one-based scheme the caller uses). */
  page: number;
  /** Page value treated as the first/reset page. */
  firstPage: number;
  /** Latest page of items returned by the data hook. */
  items: T[];
  /** Extract a stable id from each item for dedup. */
  idOf: (item: T) => string;
  /** When true, accumulation is paused (e.g. search mode). */
  paused?: boolean;
  /** Optional: only update when items shallow-id-differ from previous accumulated. */
  skipIdenticalFirstPage?: boolean;
}

/**
 * Maintains an accumulated list across paginated fetches with id-based dedup.
 * On `firstPage`, replaces; on later pages, appends new ids only.
 */
export function useAccumulatedList<T>({
  page,
  firstPage,
  items,
  idOf,
  paused,
  skipIdenticalFirstPage,
}: UseAccumulatedListOptions<T>): T[] {
  const [accumulated, setAccumulated] = useState<T[]>([]);

  useEffect(() => {
    if (paused) return;
    if (page === firstPage) {
      if (skipIdenticalFirstPage) {
        setAccumulated((prev) => {
          if (
            prev.length === items.length &&
            prev.every((p, i) => idOf(p) === idOf(items[i] as T))
          ) {
            return prev;
          }
          return items;
        });
      } else {
        setAccumulated(items);
      }
    } else if (items.length > 0) {
      setAccumulated((prev) => {
        const ids = new Set(prev.map(idOf));
        const merged = [...prev];
        for (const c of items) {
          const id = idOf(c);
          if (!ids.has(id)) {
            merged.push(c);
            ids.add(id);
          }
        }
        if (
          skipIdenticalFirstPage &&
          merged.length === prev.length &&
          merged.every((m, i) => idOf(m) === idOf(prev[i] as T))
        ) {
          return prev;
        }
        return merged;
      });
    }
  }, [items, page, firstPage, paused, idOf, skipIdenticalFirstPage]);

  return accumulated;
}
